import { memo } from 'react';

import { StudioVisualizationType } from '@blockworks/platform/api/research/studio';
import { Text } from '@blockworks/ui';
import { Bit } from '@blockworks/ui/factory';

import { useVisualizationOptionsContext, VisualizationOptionsProvider } from '../../context';

import type { VisualizationRendererProps } from './components/visualization-renderer/visualization-renderer.model';
import { useIsVisible } from './utils/use-is-visible';
import {
    ObscuredVisualization,
    PreloadedVisualization,
    PreloadedWithExecutionVisualization,
    VisualizationRenderer,
} from './components';
import { useGetMergedVisualizationAndExecution } from './utils';

type VisualizationProps = {
    id: string;
} & Pick<VisualizationRendererProps, 'componentSlots'>;

interface VisualizationComponent extends React.NamedExoticComponent<VisualizationProps> {
    /**
     *  * A context provider for visualization options and features such as layout options,
     * ability to export data, table search, etc.
     */
    OptionsProvider: typeof VisualizationOptionsProvider;
    /**
     * A visualization renderer that expects visualization and execution data to be passed in,
     * then it will handle parameterizing and rendering the visualization.
     */
    Renderer: typeof VisualizationRenderer;
    /**
     * A visualization abstraction that is preloaded with visualization data,
     * and fetches the execution data on demand, given the `lastExecutionId`
     * from the given visualization data. Then it will parameterize and render the visualization.
     **/
    Preloaded: typeof PreloadedVisualization;
    /**
     * A visualization abstraction that is preloaded with visualization and execution data.
     * This is useful for locked dashboards, where the execution data cannot be fetched in runtime.
     */
    PreloadedWithExecution: typeof PreloadedWithExecutionVisualization;
    /**
     * A renderer for a visualization screenshot. This is used for locked dashboards,
     * so locked visualizations can be obscured (aka blurred).
     */
    Obscured: typeof ObscuredVisualization;
}

/**
 * The most comprehensive and responsible visualization component for rendering visualizations.
 * It handles fetching the visualization data, execution data, parameterizing and rendering the visualization.
 */
const Visualization = memo(
    function Visualization({ id, componentSlots }: VisualizationProps) {
        const { containerRef, isViewable, wasViewable } = useIsVisible(false);
        const { features } = useVisualizationOptionsContext();
        const { data, visualization, execution } = useGetMergedVisualizationAndExecution({
            visualizationId: id,
            sameOriginProxy: features.dangerouslyProxyRequests,
        });

        const isHidden = data?.type === StudioVisualizationType.Table ? !wasViewable : !isViewable;

        if (visualization?.isLoading || execution?.isLoading || isHidden) {
            return (
                <Bit.div
                    ref={containerRef}
                    display="flex"
                    w="full"
                    h="full"
                    justifyContent="center"
                    alignItems="center"
                    pos="relative"
                />
            );
        }

        /** this can happen if (1) the fetch fails or (2) if a visualization was deleted before it was removed from a dashboard */
        if ((data && data?.status === 0) || !data?._meta?.success || !data?.type) {
            if (!data?.type) {
                console.warn('Visualization type not found');
            }
            const { HeaderEnd } = componentSlots ?? {};

            return (
                <Bit.div
                    display="flex"
                    w="full"
                    h="full"
                    justifyContent="center"
                    alignItems="center"
                    pos="relative"
                    ref={containerRef}
                >
                    {!!HeaderEnd && (
                        <Bit.div pos="absolute" top={2} right={2}>
                            <HeaderEnd removeOnly />
                        </Bit.div>
                    )}
                    <Bit.div display="flex" justifyContent="center" alignItems="center" w="full" h="full">
                        <Text center>{data?.message ?? 'Visualization not found.'}</Text>
                    </Bit.div>
                </Bit.div>
            );
        }

        return (
            <Bit.div
                w="full"
                h="full"
                justifyContent="center"
                alignItems="center"
                pos="relative"
                className="scroller"
                ref={containerRef}
            >
                <VisualizationRenderer componentSlots={componentSlots} visualization={data} />
            </Bit.div>
        );
    },
    (prev, next) => prev.id === next.id,
) as VisualizationComponent;

Visualization.Renderer = VisualizationRenderer;
Visualization.OptionsProvider = VisualizationOptionsProvider;
Visualization.Preloaded = PreloadedVisualization;
Visualization.PreloadedWithExecution = PreloadedWithExecutionVisualization;
Visualization.Obscured = ObscuredVisualization;

export { Visualization };
